/**
 * Order feature constants.
 */

/** Custom type key for order custom fields in Commercetools. */
export enum CustomTypeKey {
    ORDER_CUSTOM_DATA = 'orderCustomData',
}

/** Commercetools product type IDs for line item classification. */
export const PASS_PRODUCT_TYPE_ID = import.meta.env.VITE_PASS_PRODUCT_TYPE_ID;
export const ADDON_PRODUCT_TYPE_ID = import.meta.env.VITE_ADDON_PRODUCT_TYPE_ID;

/** Language locales used in the application. */
export const LOCALE_EN = 'en';

/** Expand parameters for fetching order details. */
export const ORDER_EXPAND = [
    'lineItems[*].discountedPricePerQuantity[*].discountedPrice.includedDiscounts[*].discount',
    'lineItems[*].price.discounted.discount',
    'discountCodes[*].discountCode',
    'paymentInfo.payments[*]',
];

/** Minimum loading time (ms) for confirmation pages to avoid flash. */
export const MIN_LOAD_TIME = 2000;

export const ORDER_POLLING = {
    // Max 10 attempts with exponential backoff: 500ms, 1s, 2s, 2s, ... ≈ 15 seconds total
    MAX_ATTEMPTS: 10,
    INITIAL_DELAY_MS: 500,
    MAX_DELAY_MS: 2000,
} as const;

/** Breakpoint for sidebar layout transition (matching lg + 1px) */
export const SIDEBAR_BREAKPOINT = '(min-width: 1025px)';

export const PAYMENT_METHOD = {
    INVOICE: 'Invoice',
    STRIPE: 'Stripe',
    CARD: 'card',
} as const;

export const PAYMENT_STATUS = {
    SUCCESS: 'Success',
    CHARGE: 'Charge',
} as const;

export const ORDER_FIELD_KEYS = {
    IS_AMBER_COUNTRY: 'isOrderFromAmberCountry',
    FIRST_NAME: 'firstName',
    LAST_NAME: 'lastName',
    USER_PROFILE_ID: 'userProfileId',
    LEGAL_ENTITY_CODE: 'legalEntityCode',
    ASSOCIATION_KEY: 'associationKey',
    INVOICE_TIMELINE: 'invoicePaymentTimeline',
    EVENT_VENUE: 'event-venue',
    EVENT_LOCATION: 'event-location',
    START_DATE: 'startDate',
    END_DATE: 'endDate',
    CONFIG_PRODUCT: 'configurationProduct',
    VENUE: 'venue',
    STEP_TITLE: 'stepTitle',
    STEP_DESCRIPTION: 'stepDescription',
    CURRENCY_AUTO: 'USD',
    /** Category custom field: 'enable' | 'disable' */
    HOTEL_MAP_STATUS: 'hotelMapStatus',
    /** Category custom field: URL string for the hotel map booking page */
    HOTEL_MAP_URL: 'hotelMapUrl',
} as const;

export const ORDER_STORAGE_KEYS = {
    ORDER_ID: 'orderId',
    CART_ID: 'cartId',
    PRODUCT_STORE_MAP: 'productStoreMap',
} as const;

export const ORDER_PARAM_KEYS = {
    SESSION_ID: 'session_id',
    PAYMENT_INTENT: 'payment_intent',
    ORDER_ID: 'orderId',
} as const;

export const ORDER_ATTRIBUTE_KEYS = {
    CARD_NEXT_STEPS: 'payByCardNextSteps',
    INVOICE_NEXT_STEPS: 'payByInvoiceNextSteps',
} as const;

export const ORDER_CT_ACTIONS = {
    ADD_PAYMENT: 'addPayment',
    SET_CUSTOM_TYPE: 'setCustomType',
    SET_ORDER_NUMBER: 'setOrderNumber',
} as const;

export const ORDER_CT_TYPES = {
    CART: 'cart',
    PAYMENT: 'payment',
    TYPE: 'type',
} as const;

export const ORDER_QUERY_PARAMS = {
    SORT_DESC: 'createdAt desc',
    LIMIT_1: '1',
    EXPAND_PAYMENTS: 'paymentInfo.payments[*]',
} as const;

export const ORDER_DATE_OPTIONS: Intl.DateTimeFormatOptions = { 
    day: 'numeric', 
    month: 'short', 
    year: 'numeric' 
};

export const ORDER_TEXTS = {
    LOADING: 'Loading order details...',
    NOT_FOUND: 'Order not found',
    RETURN_HOME: 'Return to Home',
    THANK_YOU_TITLE: 'Thank You! Your Booking is Reserved.',
    ORDER_NUMBER_PREFIX: 'Your order number is',
    INVOICE_PENDING_TITLE: 'Payment Pending: Action Required',
    INVOICE_PENDING_DESC: "We've received your order and it's currently being reviewed by our team. Once approved, you'll receive a confirmation email and invoice.",
    INVOICE_DUE_PREFIX: "You'll then have",
    INVOICE_DUE_SUFFIX: 'to complete payment before your tickets are released.',
    STANDARD_PENDING_DESC: "We've received your order and it's now being reviewed by our team. You'll receive a confirmation email once it's fully approved.",
    NEXT_STEPS_TITLE: 'Next steps:',
    YOUR_BOOKING_TITLE: 'Your booking:',
    NO_BOOKING_DETAILS: 'No booking details available.',
    SHARE_TITLE: 'Share your attendance',
    VERIFICATION_FALLBACK: 'your email',
    PASS_LABEL: 'Pass',
    PASSES_LABEL: 'Passes',
    ADDONS_LABEL: 'Add ons',
    UNKNOWN_PRODUCT: 'Unknown Product',
    DEFAULT_DUE_DURATION: '30 days',
    HOTEL_MAP_TITLE: 'Check hotels nearby',
    HOTEL_MAP_BUTTON: 'Book now',
    HOTEL_MAP_ARIA: 'Book a hotel nearby',
    HOTEL_MAP_DEFAULT_URL: 'https://informaconnect.com/land-development/hotel-map/',
    SOCIAL_ALTS: {
        TWITTER: 'Twitter',
        LINKEDIN: 'LinkedIn',
        FACEBOOK: 'Facebook',
        SUCCESS: 'Success',
    },
    ERRORS: {
        ID_NOT_FOUND: 'Order ID not found in URL or session',
        FETCH_FAILED: 'Failed to load order data. Please try again later.',
        INVOICE_FETCH_FAILED: 'Failed to load invoice order details.',
        CREATE_FAILED: 'Failed to create order',
        UPDATE_FAILED: 'Failed to update order custom fields',
        CART_RESOLVE_FAILED: 'Failed to resolve cart version',
        UNKNOWN_CART_ERROR: 'Unknown cart error',
        INVALID_CONSOLIDATED_ACTION: 'Invalid action in consolidated update',
        POLLING_TIMEOUT: 'Order synchronization timed out. Please try again.',
    }
} as const;

export const ORDER_NEXT_STEPS = {
    VERIFICATION: {
        title: 'Booking verification',
        description: 'Verification link sent to',
        icon: 'file',
    },
    APPROVAL: {
        title: 'Order approval details',
        description: 'Wait for email confirmation',
        icon: 'pass',
    },
    INVOICE_DOWNLOAD: {
        title: 'Download invoice',
        description: 'Invoice sent to your email',
        icon: 'filecut',
    },
    CONFIRMATION: {
        title: 'Order confirmation',
        description: 'Confirmation number',
        icon: 'pass',
    },
} as const;
